import { CompanyType } from './company.model';
import type { LicenseBatchDto, ReleaseBundleDto } from './license.model';
import type { ProfileType } from './user.model';

export type DeviceProjection = 'BRIEF' | 'FULL';

export interface DeviceUpdate {
  nameAlias?: string;
  description?: string;
  companyId?: string;
  softwareVersionOverrides?: SoftwareVersionOverridesRequest;
}

export interface SoftwareVersionOverridesRequest {
  artifactOverrides: ArtifactOverrideRequest[];
  bundleOverrides: BundleOverrideRequest[];
}

export interface SoftwareVersionOverridesDto {
  artifactOverrides?: ArtifactResponse[];
  bundleOverrides?: ReleaseBundleDto[];
  /** Overridden artifacts and bundles gone from Spadok; listed above with their ids only. */
  unresolvedOverrideIds?: string[];
}

export interface BundleOverrideRequest {
  id: string;
}

export interface AircraftModel {
  id?: string;
  key?: string;
  name?: string;
  description?: string;
  hardwareRevision?: number;
  hardwareRevisionString?: string;
}

export interface Autopilot {
  serialNumber?: string;
}

export interface Software {
  qosVersion?: string;
}

export type DeviceDetailsType =
  | 'App'
  | 'QsAntennaTracker'
  | 'QsCuas'
  | 'QsGcs'
  | 'QsTrinityPro'
  | 'QsUav';

export interface DeviceDetailsBase<TType extends DeviceDetailsType> {
  type: TType;
}

export interface AppDeviceDetailsDto extends DeviceDetailsBase<'App'> {}

export interface QsAntennaTrackerDetailsDto
  extends DeviceDetailsBase<'QsAntennaTracker'> {
  hardwareRevision?: string;
  unitId?: number;
  capVersion?: string;
}

export interface QsCuasDetailsDto extends DeviceDetailsBase<'QsCuas'> {
  unitId?: number;
  hardware_type?: string;
}

export interface QsGcsDetailsDto extends DeviceDetailsBase<'QsGcs'> {
  operatingSystem?: string;
}

export interface QsUavDetailsDto extends DeviceDetailsBase<'QsUav'> {
  aircraftModel?: AircraftModel;
  unitId?: number;
  autopilot?: Autopilot;
  software?: Software;
}

export interface QsTrinityProDetailsDto
  extends DeviceDetailsBase<'QsTrinityPro'> {
  aircraftModel?: AircraftModel;
  unitId?: number;
  autopilot?: Autopilot;
  software?: Software;
  licenseKey?: string;
}

export type DeviceDetailsDto =
  | AppDeviceDetailsDto
  | QsAntennaTrackerDetailsDto
  | QsCuasDetailsDto
  | QsGcsDetailsDto
  | QsTrinityProDetailsDto
  | QsUavDetailsDto;

export interface GeneralInfoItem {
  label?: string;
  value?: string;
}

export interface DeviceSummaryFact {
  label?: string;
  value?: string;
}

export interface DeviceSummary {
  defaultName?: string;
  model?: string;
  modelName?: string;
  unitId?: string;
  facts?: DeviceSummaryFact[];
}

export interface DeviceManagementFeatures {
  hasLicenses?: boolean;
  hasSessions?: boolean;
  hasParts?: boolean;
  hasPartMaintenance?: boolean;
  multiConfigurationSupport?: string[];
}

/** A confirmation of the device association the certificate signs in with. */
export interface CertificateApprovalDto {
  approvedAt?: string;
  approvedBy?: Partial<ProfileType>;
  role?: string;
  privilegedRole?: string;
  note?: string;
}

export interface CertificateDto {
  thumbprint?: string;
  createdAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  // The fields below are present only for privileged users.
  certificate?: string;
  revokedBy?: Partial<ProfileType>;
  /** The latest approvals first. */
  approvals?: CertificateApprovalDto[];
  /** Set when the certificate couldn't be loaded. */
  failure?: string;
}

export interface DeviceDto {
  projection?: DeviceProjection;
  // Legacy field kept for report payloads and older backend responses.
  deviceType?: string;
  deviceId?: string;
  nameAlias?: string;
  description?: string;
  // Legacy field kept while detail/report payloads migrate to deviceSummary.
  generalInfoItems?: GeneralInfoItem[];
  deviceSummary?: DeviceSummary;
  deviceManagementFeatures?: DeviceManagementFeatures;
  softwareVersionOverrides?: SoftwareVersionOverridesDto;
  licenseRedemptionInfo?: LicenseRedemptionInfoDto;
  properties?: Record<string, unknown>;
  certificates?: CertificateDto[];
  company?: Partial<CompanyType>;
  details?: DeviceDetailsDto;
  licenseBatches?: LicenseBatchDto[];
  deviceHistory?: DeviceHistoryDto;
}

export type SoftwareSelectionReason =
  | 'LATEST_RELEASE'
  | 'ARTIFACT_OVERRIDE'
  | 'LICENSE_BATCH_ARTIFACT_OVERRIDE'
  | 'BUNDLE_OVERRIDE'
  | 'LICENSE_BATCH_BUNDLE_OVERRIDE'
  | 'SCOPED_THIRD_PARTY_CLIENT'
  | 'DEFAULT_THIRD_PARTY_CLIENT'
  | 'DEVICE_DEDICATED_THIRD_PARTY_CLIENT';

export const THIRD_PARTY_SOFTWARE_SELECTION_REASONS: ReadonlySet<SoftwareSelectionReason> =
  new Set([
    'SCOPED_THIRD_PARTY_CLIENT',
    'DEFAULT_THIRD_PARTY_CLIENT',
    'DEVICE_DEDICATED_THIRD_PARTY_CLIENT',
  ]);

export interface SoftwareSelectionNamedEntityDto {
  id?: string;
  name?: string;
}

export interface ResolvedSoftwareArtifactDto {
  id?: string;
  version?: string;
  projectId?: string;
  projectName?: string;
  description?: string;
  scopes?: string[];
  compatibleHardwareComponents?: string[];
  artifactMaturity?: string;
  releaseStatus?: string;
  createdAt?: string;
  downloadUrl?: string;
}

export type SoftwareSelectionResult =
  | 'SUCCESS'
  | 'NO_LICENSE_BATCH'
  | 'LICENSE_BATCH_OUTSIDE_VALIDITY_PERIOD'
  | 'NO_THIRD_PARTY_CLIENT_CONFIGURED'
  | 'NO_LATEST_RELEASED_COMPONENT_IN_SPADOK'
  | 'NO_SPADOK_HARDWARE_COMPONENT'
  | 'THIRD_PARTY_TOKEN_REQUEST_FAILED'
  | 'SCOPE_NOT_FOUND'
  /** Only describing a license batch: the latest release depends on the device's hardware. */
  | 'DEPENDS_ON_DEVICE';

export interface SoftwareSelectionResolutionDto {
  component?: SoftwareSelectionNamedEntityDto;
  scope?: SoftwareSelectionNamedEntityDto;
  result?: SoftwareSelectionResult;
  spadokArtifact?: ResolvedSoftwareArtifactDto;
  thirdPartyServiceClientId?: string;
  selectionReason?: SoftwareSelectionReason;
  bundleOverride?: {
    bundle?: SoftwareSelectionNamedEntityDto;
    release?: SoftwareSelectionNamedEntityDto;
  };
  licenseBatch?: SoftwareSelectionNamedEntityDto;
}

export type LicenseRedemptionRequestType =
  | 'INDIVIDUAL_SOFTWARE'
  | 'GET_ALL_SOFTWARE'
  | 'THIRD_PARTY_SERVICE'
  | 'DESCRIBE_DEVICE_SOFTWARE_SELECTION'
  | 'DESCRIBE_LICENSE_BATCH_SOFTWARE_SELECTION';

export interface LicenseRedemptionInfoDto {
  type?: 'LicenseRedemption';
  requestType?: LicenseRedemptionRequestType;
  components?: SoftwareSelectionResolutionDto[];
}

export interface LicenseRedemptionPayloadDto extends LicenseRedemptionInfoDto {
  type: 'LicenseRedemption';
}

export interface DeviceHistoryDevicePayloadDto {
  type: 'Device';
  deviceId?: string;
  certificateThumbprint?: string;
}

export type DeviceHistoryEventPayloadDto =
  | LicenseRedemptionPayloadDto
  | DeviceHistoryDevicePayloadDto;

export type DeviceHistoryEventType =
  | 'DEVICE_CERTIFICATE_REGISTRATION'
  | 'DEVICE_CERTIFICATE_REVOCATION'
  | 'DEVICE_AUTHORIZATION'
  | 'LICENSE_REDEMPTION';

export interface DeviceHistoryEventDto<
  TPayload extends DeviceHistoryEventPayloadDto = DeviceHistoryEventPayloadDto,
> {
  time?: string;
  eventType?: DeviceHistoryEventType;
  error?: string;
  payload?: TPayload;
}

export interface DeviceHistoryDto {
  events?: DeviceHistoryEventDto[];
  lastSeenAt?: string;
  createdAt?: string;
  creator?: Partial<ProfileType>;
  latestGetAllSoftware?: DeviceHistoryEventDto<LicenseRedemptionPayloadDto>;
}

export interface DeviceListDto {
  devices?: DeviceDto[];
}

export interface DeviceTemplateSummaryDto {
  id?: string;
  name?: string;
  description?: string;
}

export interface DeviceTemplateListDto {
  templates?: DeviceTemplateSummaryDto[];
}

export enum DeviceUnitIdStrategy {
  FIRST_AVAILABLE = 'FIRST_AVAILABLE',
  RANDOM_IN_RANGE = 'RANDOM_IN_RANGE',
  LARGEST_PLUS_ONE = 'LARGEST_PLUS_ONE',
  EXPLICIT = 'EXPLICIT',
}

export interface CreateDeviceFromTemplateRequest {
  strategy?: DeviceUnitIdStrategy;
  unitId?: number;
}

export type PartType =
  | 'body'
  | 'left_wing'
  | 'right_wing'
  | 'rear'
  | 'payload'
  | 'battery'
  | 'other';

export interface PartMetricDto {
  type?: string;
  unit?: string;
  value?: number;
  valueThreshold?: number;
}

export interface MaintenanceEventDto {
  id?: string;
  typeOfMaintenance?: string;
  description?: string;
  maintainer?: string;
  eventTime?: string;
  detailedDescription?: string;
  detailedMaintainer?: string;
  attachments?: string[];
}

export interface Quantity {
  value?: number;
  unit?: string;
}

export type PartDetailsType = 'QsUavPart' | 'QsUgvPart';
export type PartConfiguration = 'UNSPECIFIED' | 'VECTOR' | 'SCORPION';

export interface PartDetailsBase<TType extends PartDetailsType> {
  type: TType;
}

export interface QsUavPartDetailsDto extends PartDetailsBase<'QsUavPart'> {
  partType?: string;
  partSubType?: string;
  partId?: string;
  configuration?: PartConfiguration;
}

export interface QsUgvPartDetailsDto extends PartDetailsBase<'QsUgvPart'> {
  usageMetric?: Quantity;
}

export type PartDetailsDto = QsUavPartDetailsDto | QsUgvPartDetailsDto;

export interface PartDto {
  partId?: string;
  type?: PartType;
  details?: PartDetailsDto;
  metrics?: PartMetricDto[];
  maintenanceEvents?: MaintenanceEventDto[];
  /** Only filled in the company parts list. */
  lastConnectedDevice?: DeviceDto;
}

export interface PartListDto {
  parts?: PartDto[];
}

export type SessionDetailsType = 'QsUavFlight';

export interface SessionDetailsBase<TType extends SessionDetailsType> {
  type: TType;
}

export interface QsUavFlightDetailsDto
  extends SessionDetailsBase<'QsUavFlight'> {
  configuration?: PartConfiguration;
}

export type SessionDetailsDto = QsUavFlightDetailsDto;

export interface SessionDto {
  sessionId?: string;
  startTime?: string;
  durationInSeconds?: number;
  parts?: PartDto[];
  device?: DeviceDto;
  details?: SessionDetailsDto;
}

export interface SessionListDto {
  sessions?: SessionDto[];
}

export interface UserReference {
  id: string; // UUID
  name: string;
  email: string;
}
export interface ArtifactResponse {
  id: string;
  fullFileName?: string;
  fileName?: string;
  description?: string;
  projectName: string;
  projectId: string;
  blobPath: string;
  downloadUrl: string;
  version: string;
  releaseStatus?: string;
  scopes: string[];
  compatibleHardwareComponents: string[];
  artifactMaturity: 'DEV' | 'RC';
  uploadStatus: 'UPLOADING' | 'UPLOADED' | 'FAILED';
  createdAt: string; // ISO date string
  createdBy?: UserReference;
  updatedAt?: string; // ISO date string
  updatedBy?: UserReference;
}
export type ArtifactOverrideRequest = Omit<ArtifactResponse, 'fullFileName'>;

export interface ArtifactVM extends ArtifactResponse {
  versionAndScopes: string;
}
